import React, { FC, useEffect, useMemo } from 'react';
import { Alert } from '@rbx/ui';
import { useLocalization, useTranslation } from '@rbx/intl';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import {
  useProvidedConversationReducer,
  ConversationError,
  MessageHistoryItem,
  conversationErrorSeverity,
  RequestId,
} from '@rbx/conv-ai-provider';
import useBotAlertStyles from './BotAlert.styles';
import getMessageLogParams from '../utils/getMessageLogParams';

type ConversationErrorAlertProps = {
  error: ConversationError;
  requestId?: RequestId;
  message?: MessageHistoryItem;
  skipLogging?: boolean;
};

const EasterMaxUserRateLimitHits = 10; // Easter egg, yell at @jamiewhite

const ConversationErrorAlert: FC<React.PropsWithChildren<ConversationErrorAlertProps>> = ({
  error,
  message,
  requestId,
  skipLogging,
}) => {
  const { locale } = useLocalization();
  const { translate } = useTranslation();
  const {
    state: { conversationId, userDailyRateLimit, userRateLimitErrorCount },
  } = useProvidedConversationReducer();
  const {
    classes: { alert },
  } = useBotAlertStyles();
  const alertMessage = useMemo(() => {
    if (error === 'ContentFilterFailed') {
      return translate(`AlertMessage.ContentFilter`);
    }
    if (error === 'ServiceRateLimitExceeded') {
      return translate(`AlertMessage.ServiceRateLimit`);
    }
    if (error === 'TokenLimitExceeded') {
      return translate(`AlertMessage.TokenLimitExceeded`);
    }
    if (error === 'UserRateLimitExceeded') {
      // Easter egg, yell at @jamiewhite
      if (userRateLimitErrorCount > EasterMaxUserRateLimitHits)
        return "You're tearing me apart, Lisa!";
      // End easter egg
      if (userDailyRateLimit)
        return translate(`AlertMessage.UserRateLimit`, {
          maxMessagesPerDay: userDailyRateLimit.toString(),
        });
      return translate(`AlertMessage.RateLimitNoLimitSpecified`);
    }
    return translate(`AlertMessage.Generic`);
  }, [error, userDailyRateLimit, userRateLimitErrorCount, translate]);
  useEffect(() => {
    if (skipLogging || !error) return;
    unifiedLoggerClient.logImpressionEvent({
      eventName: DocsAssistantEvent.MessageError,
      parameters: {
        error,
        ...getMessageLogParams({
          message,
          conversationId,
          locale,
        }),
        ...(requestId ? { requestId } : {}),
      },
    });
    // do not duplicate log. Run only once on error mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Alert
      aria-label={translate('Label.Alert')}
      className={alert}
      severity={conversationErrorSeverity[error]}>
      {alertMessage}
    </Alert>
  );
};

export default ConversationErrorAlert;
